import React, { useState } from 'react'
import { mutate } from 'swr'
import { readAllNotificationUser } from '../../api/LogsCRUD'
import useAccessToken from '../../hooks/AccessToken'
import moment from 'moment-timezone'
import useModalEffect from '../../hooks/useModalEffect'
import Swal from 'sweetalert2'
import { Notification } from '../../interfaces/NotificationLog/notification'
import useNotificationStore from '../../stores/Notification'
import { useIntl } from 'react-intl'

interface NotificationLogProps {
    dataNotif: Notification[]
    isLoadingNotif: boolean
    mutateNotif: () => void
}

const API_URL = process.env.REACT_APP_API_URL

const getIconByType = (type: string) => {
    switch (type) {
        case 'TERMIN.REMINDER':
            return 'fas fa-money-bill-wave'
        case 'REIMBURSEMENT.INFO':
            return 'fas fa-wallet'
        case 'LOG.INFO':
            return 'fas fa-file-alt'
        case 'MEETING.INFO':
            return 'fas fa-calendar-check'
        case 'PIPELINE.INFO':
            return 'fas fa-stream'
        default:
            return 'fas fa-bell'
    }
}

const NotificationLog = ({ dataNotif, isLoadingNotif, mutateNotif }: NotificationLogProps) => {
    const [searchTerm, setSearchTerm] = useState('')
    const token = useAccessToken()
    const { detailNotification, setDetailNotification, setShowDetailNotification, showDetailNotification } = useNotificationStore()
    const sortedDataNotif = Array.isArray(dataNotif) ? dataNotif.sort((a: Notification, b: Notification) => {
        return new Date(b.createdtime).getTime() - new Date(a.createdtime).getTime()
    }) : []
    const intl = useIntl()

    const filteredNotifications = Array.isArray(sortedDataNotif) ? sortedDataNotif.filter((notif: Notification) =>
        notif?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        notif?.body?.toLowerCase().includes(searchTerm.toLowerCase())
    ) : []
    const [isLoading, setIsLoading] = useState(false)

    useModalEffect({
        showModal: showDetailNotification,
        setShowModal: setShowDetailNotification,
        modalId: `detail-notification-log-modal-${detailNotification?.id}`
    })

    const unreadCount = Array.isArray(dataNotif) ? dataNotif.filter((notif: Notification) => !notif?.readtime).length : 0

    const handleViewAll = async () => {
        try {
            setIsLoading(true)
            const response = await readAllNotificationUser(token)
            if (response.status) {
                mutateNotif()
                Swal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: intl.formatMessage({ id: 'NOTIFICATION.SUCCESS.READ_ALL' }),
                })
            }
        } catch (error) {
            console.log(error)
            Swal.fire({
                icon: 'error',
                title: 'Oops...',
                text: 'Something went wrong!',
            })
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <>
            <div className="menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-600 menu-state-bg-light-primary fw-bold py-4 show" style={{ minWidth: "350px" }}>
                <div className="menu-item px-3">
                    <div className="px-2 d-flex justify-content-between pb-4">
                        <span className="fs-5">Notifications</span>
                        <span className="badge badge-light-primary">{unreadCount} new</span>
                    </div>
                </div>
                {(isLoadingNotif || isLoading) && (
                    <div className="loading-overlay" style={{ minWidth: '350px' }}>
                        <div className="loader"></div>
                    </div>
                )}
                {/* Search input */}
                <div className="menu-item px-3">
                    <div className="position-relative mb-3">
                        <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Search notifications..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <span className="position-absolute top-50 end-0 translate-middle-y pe-2">
                            <i className="fas fa-search text-muted fs-6"></i>
                        </span>
                    </div>
                </div>

                <div className="separator my-2"></div>

                {/* Notification Items */}
                <div className="menu-item" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {filteredNotifications?.length > 0 ? (
                        filteredNotifications?.map((notif: Notification) => {
                            return (
                                <div key={notif.id} className='px-3'>
                                    <div className={`d-flex flex-column align-items-center py-4 justify-content-between px-3 mb-2 ${!notif.readtime ? 'bg-light-warning rounded' : ''}`} style={{ width: '100%' }}>
                                        <div className="d-flex justify-content-between align-items-center" style={{ width: '100%' }}>
                                            <div className="symbol me-4">
                                                <span className="symbol-label bg-light-primary">
                                                    <i className={`${getIconByType(notif.type)} text-primary fs-6`}></i>
                                                </span>
                                            </div>
                                            <div className="mb-0 cursor-pointer" style={{ width: '100%' }} onClick={() => {
                                                setDetailNotification(notif)
                                                setShowDetailNotification(true)
                                            }}>
                                                <div className="fs-6 text-gray-800 text-hover-primary fw-bold">{notif.title}</div>
                                                <div className="text-gray-400 fs-7">{notif.body.length > 50 ? notif.body.substring(0, 50) + '...' : notif.body}</div>
                                            </div>
                                        </div>
                                        <span className="w-100 text-muted flex-end fs-7 fw-bold text-end fst-italic">{moment(notif.createdtime).fromNow()}</span>
                                    </div>
                                </div>
                            )
                        })
                    ) : (
                        <div className="text-center text-muted py-4">
                            No notifications found
                        </div>
                    )}
                </div>

                {
                    unreadCount !== 0 && (
                        <>
                            <div className="separator my-2"></div>
                            <div className="menu-item px-3 text-center">
                                <button className="btn btn-light-primary btn-sm px-5" onClick={handleViewAll} disabled={isLoading}>Read All</button>
                            </div>
                        </>
                    )
                }
            </div>
        </>
    )
}

export default NotificationLog